import React, { useEffect, useMemo, useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SERVER_URL } from "../api";
import { getGroupById } from "../api/group";
import { getGameById } from "../api/game";
import { getQuizById } from "../api/quiz";
import { setGroup } from "../redux/actions";
import { ROUTES } from "../utils/constants";
import { groupLinkedGameId, resetPlaySessionToEnterCode } from "../utils/resetPlaySession";
import { updateStats } from "../utils/helpers";
import { postKpiEvent } from "../api/kpi";
import { bumpSessionKpi } from "../utils/sessionKpiStore";
import { EVENELY_LOGO_BLACK_URL, EVENELY_LOGO_WHITE_URL } from "../utils/brand";
import { DEFAULT_ER_ACCENT } from "../utils/gameColors";
import { collectQuizIdsFromPages, computeEscapeRoomMaxScore, pagesHavePlayableContent } from "../utils/escapeRoomMaxScore";

const FONT = '"League Spartan", system-ui, -apple-system, sans-serif';
const FALLBACK_SCORE_MAX = 110;

const getLevelInfo = (scoreRaw, scoreMax, t) => {
	const s = Number.isFinite(scoreRaw) ? scoreRaw : 0;
	const max = Math.max(1, Number.isFinite(scoreMax) && scoreMax > 0 ? scoreMax : FALLBACK_SCORE_MAX);
	const pct = s / max;
	if (s < 1) return { key: "fail", title: t("RESULTS.LEVEL_FAIL_TITLE"), subtitle: t("RESULTS.LEVEL_FAIL_SUB"), tone: "fail" };
	if (s >= max) return { key: "master", title: t("RESULTS.LEVEL_MASTER_TITLE"), subtitle: t("RESULTS.LEVEL_MASTER_SUB"), tone: "top" };
	if (pct >= 0.9) return { key: "experto", title: t("RESULTS.LEVEL_EXPERT_TITLE"), subtitle: t("RESULTS.LEVEL_EXPERT_SUB"), tone: "top" };
	if (pct >= 0.7) return { key: "muy-buen-desempeno", title: t("RESULTS.LEVEL_GREAT_TITLE"), subtitle: t("RESULTS.LEVEL_GREAT_SUB"), tone: "high" };
	if (pct >= 0.4) return { key: "competente", title: t("RESULTS.LEVEL_OK_TITLE"), subtitle: t("RESULTS.LEVEL_OK_SUB"), tone: "mid" };
	if (pct >= 0.1) return { key: "en-proceso", title: t("RESULTS.LEVEL_PROGRESS_TITLE"), subtitle: t("RESULTS.LEVEL_PROGRESS_SUB"), tone: "low" };
	return { key: "minimo", title: t("RESULTS.LEVEL_MIN_TITLE"), subtitle: t("RESULTS.LEVEL_MIN_SUB"), tone: "low" };
};

function Results({ intl }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const t = (id, values) => intl.formatMessage({ id }, values);

	const reduxGroup = useSelector((state) => state.group);
	const reduxGame = useSelector((state) => state.game);
	const [group, setCurrentGroup] = useState(reduxGroup);
	const [escapeRoomPages, setEscapeRoomPages] = useState(() => reduxGame?.escapeRoom?.pages || null);
	const [quizzesById, setQuizzesById] = useState({});
	const [img, setImg] = useState(null);
	const [time, setTime] = useState(() => Number(reduxGroup?.time) || 0);
	const [groupName, setGroupName] = useState(null);

	const scoreMax = useMemo(() => {
		if (!Array.isArray(escapeRoomPages) || escapeRoomPages.length === 0) return FALLBACK_SCORE_MAX;
		const quizIds = collectQuizIdsFromPages(escapeRoomPages);
		const quizzesReady = quizIds.length === 0 || quizIds.every((id) => quizzesById[id]);
		const computed = computeEscapeRoomMaxScore(
			escapeRoomPages,
			quizzesReady ? quizzesById : {}
		);
		if (!Number.isFinite(computed) || computed <= 0) return FALLBACK_SCORE_MAX;
		return computed;
	}, [escapeRoomPages, quizzesById]);

	const score = Number(group?.score ?? 0);
	const isFailure = score < 1;
	const level = getLevelInfo(score, scoreMax, t);
	const scorePct = Math.min(100, Math.max(0, (score / Math.max(1, scoreMax)) * 100));

	const initials = useMemo(() => {
		const n = String(groupName || "").trim();
		if (!n) return "EV";
		const parts = n.split(/\s+/).filter(Boolean);
		if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
		return n.slice(0, 2).toUpperCase();
	}, [groupName]);

	const mesaLabel = useMemo(() => {
		const table = group?.tableNum;
		if (table == null || table === "") return null;
		const s = String(table).trim();
		if (/^\d{1,6}$/.test(s)) return t("RESULTS.TABLE", { n: s });
		return null;
	}, [group?.tableNum, intl.locale]);

	const mm = Math.floor(time / 60).toString().padStart(2, "0");
	const ss = (time % 60).toString().padStart(2, "0");

	useEffect(() => {
		const gid = group?._id;
		if (!gid) {
			resetPlaySessionToEnterCode(dispatch, navigate);
			return;
		}
		let cancelled = false;
		getGroupById(gid)
			.then((res) => {
				if (cancelled) return;
				if (res.status !== 200) {
					resetPlaySessionToEnterCode(dispatch, navigate);
					return;
				}
				const fresh = res.data;

				const reduxTime = Number(group?.time) || 0;
				const serverTime = Number(fresh?.time) || 0;
				const bestTime = Math.max(reduxTime, serverTime);

				const reduxScore = Number(group?.score) || 0;
				const serverScore = Number(fresh?.score) || 0;
				const bestScore = Math.max(reduxScore, serverScore);

				setCurrentGroup({ ...fresh, score: bestScore, time: bestTime });
				setGroupName(fresh?.fullName || null);
				setImg(fresh?.image ? `${SERVER_URL}/${fresh.image}` : null);
				setTime((prev) => Math.max(prev, serverTime));

				const merged = {
					...fresh,
					score: bestScore,
					time: bestTime,
					lastVisited: ROUTES.RESULTS,
					finished: true,
				};
				dispatch(setGroup(merged));
				updateStats(merged);
				try {
					const gameScopeId = String(merged?.game?._id || merged?.game || "");
					const groupId = String(merged?._id || "");
					if (gameScopeId && groupId) {
						bumpSessionKpi("finish");
						postKpiEvent({
							gameId: gameScopeId,
							groupId,
							eventType: "finish",
							reason: "results",
							totalScoreSnapshot: Number(merged?.score ?? null),
							timestamp: new Date().toISOString(),
						}).catch(() => {});
					}
				} catch (_) {}
			})
			.catch(() => {
				if (!cancelled) resetPlaySessionToEnterCode(dispatch, navigate);
			});
		return () => {
			cancelled = true;
		};
	}, [dispatch, navigate, group?._id]);

	useEffect(() => {
		const gameId = groupLinkedGameId(group);
		if (!gameId) return;

		const cached = reduxGame?.escapeRoom?.pages;
		if (Array.isArray(cached) && cached.length > 0 && pagesHavePlayableContent(cached)) {
			setEscapeRoomPages(cached);
		}

		let cancelled = false;
		getGameById(gameId)
			.then((res) => {
				if (cancelled || res.status !== 200) return;
				const pages = res.data?.escapeRoom?.pages;
				if (Array.isArray(pages) && pages.length > 0) setEscapeRoomPages(pages);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [group, reduxGame?.escapeRoom?.pages]);

	useEffect(() => {
		if (!Array.isArray(escapeRoomPages) || escapeRoomPages.length === 0) return;
		const ids = collectQuizIdsFromPages(escapeRoomPages);
		if (!ids.length) return;

		let cancelled = false;
		Promise.all(
			ids.map((id) =>
				getQuizById(id)
					.then((res) => (res.status === 200 && res.data ? [id, res.data] : null))
					.catch(() => null)
			)
		).then((rows) => {
			if (cancelled) return;
			const next = {};
			rows.forEach((row) => {
				if (row) next[row[0]] = row[1];
			});
			if (Object.keys(next).length > 0) {
				setQuizzesById((prev) => ({ ...prev, ...next }));
			}
		});
		return () => {
			cancelled = true;
		};
	}, [escapeRoomPages]);

	const bg = isFailure ? "#b91c1c" : "#f4f2ee";
	const fg = isFailure ? "#ffffff" : "#141414";
	const muted = isFailure ? "rgba(255,255,255,0.72)" : "#6b6b6b";
	const line = isFailure ? "rgba(255,255,255,0.22)" : "rgba(20,20,20,0.1)";
	const scoreColor = isFailure ? "#ffffff" : "#141414";
	const brandAccent = isFailure ? "rgba(255,255,255,0.92)" : DEFAULT_ER_ACCENT;
	const barTrack = isFailure ? "rgba(255,255,255,0.22)" : "rgba(125,0,252,0.12)";
	const logoUrl = isFailure ? EVENELY_LOGO_WHITE_URL : EVENELY_LOGO_BLACK_URL;

	return (
		<div
			style={{
				minHeight: "100dvh",
				display: "flex",
				flexDirection: "column",
				background: bg,
				color: fg,
				fontFamily: FONT,
				transition: "background 0.35s ease, color 0.35s ease",
			}}
		>
			<style>{`
				@keyframes results-in {
					from { opacity: 0; transform: translateY(10px); }
					to { opacity: 1; transform: translateY(0); }
				}
				.results-in { animation: results-in 0.5s ease both; }
				.results-in-2 { animation: results-in 0.5s ease 0.07s both; }
				.results-in-3 { animation: results-in 0.5s ease 0.14s both; }
			`}</style>

			<header
				style={{
					padding: "28px clamp(20px, 5vw, 48px) 0",
					display: "flex",
					justifyContent: "center",
				}}
			>
				<img
					src={logoUrl}
					alt="Evenely"
					onError={(e) => {
						e.currentTarget.src = isFailure ? "/logo-evenely-white.webp" : "/logo.svg";
					}}
					style={{ height: 32, width: "auto", objectFit: "contain", opacity: isFailure ? 0.95 : 0.88 }}
				/>
			</header>

			<main
				style={{
					flex: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					padding: "32px clamp(20px, 5vw, 48px) 48px",
				}}
			>
				<div
					style={{
						width: "100%",
						maxWidth: 520,
					}}
				>
					{/* Equipo */}
					<div
						className="results-in"
						style={{
							display: "flex",
							alignItems: "center",
							gap: 18,
							marginBottom: 40,
						}}
					>
						<div
							style={{
								width: 72,
								height: 72,
								borderRadius: "50%",
								flexShrink: 0,
								overflow: "hidden",
								background: isFailure ? "rgba(255,255,255,0.15)" : "#f0ebff",
								border: isFailure ? "2px solid rgba(255,255,255,0.35)" : `2px solid ${brandAccent}33`,
								display: "grid",
								placeItems: "center",
							}}
						>
							{img ? (
								<img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
							) : (
								<span style={{ fontSize: 24, fontWeight: 600, color: isFailure ? "#fff" : "#141414", letterSpacing: "0.02em" }}>
									{initials}
								</span>
							)}
						</div>
						<div style={{ minWidth: 0 }}>
							{mesaLabel ? (
								<div style={{ fontSize: 13, fontWeight: 500, color: muted, marginBottom: 4 }}>{mesaLabel}</div>
							) : null}
							<h1
								style={{
									margin: 0,
									fontSize: "clamp(1.35rem, 4vw, 1.75rem)",
									fontWeight: 600,
									lineHeight: 1.2,
									letterSpacing: "-0.02em",
									color: fg,
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								{groupName || t("RESULTS.YOUR_TEAM")}
							</h1>
							<p style={{ margin: "6px 0 0", fontSize: 15, lineHeight: 1.45, color: muted, fontWeight: 400 }}>
								{level.subtitle}
							</p>
						</div>
					</div>

					{/* Puntuación principal */}
					<div className="results-in-2" style={{ marginBottom: 36 }}>
						<div
							style={{
								fontSize: 13,
								fontWeight: 500,
								color: isFailure ? muted : brandAccent,
								marginBottom: 10,
								letterSpacing: "0.04em",
								textTransform: "uppercase",
							}}
						>
							{t("RESULTS.SCORE")}
						</div>
						<div style={{ display: "flex", alignItems: "baseline", gap: 10, lineHeight: 1 }}>
							<span
								style={{
									fontSize: "clamp(4.5rem, 16vw, 6.5rem)",
									fontWeight: 700,
									letterSpacing: "-0.045em",
									color: scoreColor,
									fontVariantNumeric: "tabular-nums",
								}}
							>
								{score}
							</span>
							<span
								style={{
									fontSize: "clamp(1.25rem, 4vw, 1.75rem)",
									fontWeight: 500,
									color: muted,
									fontVariantNumeric: "tabular-nums",
								}}
							>
								/ {scoreMax}
							</span>
						</div>
						<div
							style={{
								marginTop: 22,
								height: 5,
								borderRadius: 999,
								background: barTrack,
								overflow: "hidden",
							}}
							role="progressbar"
							aria-valuenow={score}
							aria-valuemin={0}
							aria-valuemax={scoreMax}
						>
							<div
								style={{
									height: "100%",
									width: `${scorePct}%`,
									borderRadius: 999,
									background: brandAccent,
									transition: "width 0.9s cubic-bezier(0.22, 1, 0.36, 1)",
								}}
							/>
						</div>
					</div>

					{/* Tiempo + nivel */}
					<div
						className="results-in-3"
						style={{
							display: "grid",
							gridTemplateColumns: "1fr 1fr",
							gap: 24,
							paddingTop: 28,
							borderTop: `1px solid ${line}`,
						}}
					>
						<div>
							<div style={{ fontSize: 13, fontWeight: 500, color: muted, marginBottom: 8, letterSpacing: "0.04em", textTransform: "uppercase" }}>
								{t("RESULTS.TIME")}
							</div>
							<div
								style={{
									fontSize: "clamp(2rem, 6vw, 2.75rem)",
									fontWeight: 600,
									letterSpacing: "-0.02em",
									fontVariantNumeric: "tabular-nums",
									lineHeight: 1,
									color: fg,
								}}
							>
								{mm}
								<span style={{ opacity: 0.35, margin: "0 2px" }}>:</span>
								{ss}
							</div>
						</div>
						<div>
							<div style={{ fontSize: 13, fontWeight: 500, color: muted, marginBottom: 8, letterSpacing: "0.04em", textTransform: "uppercase" }}>
								{t("RESULTS.LEVEL")}
							</div>
							<div
								style={{
									fontSize: "clamp(1.35rem, 4vw, 1.85rem)",
									fontWeight: 600,
									letterSpacing: "-0.02em",
									lineHeight: 1.15,
									color: isFailure ? fg : brandAccent,
								}}
							>
								{level.title}
							</div>
						</div>
					</div>

					{isFailure ? (
						<p
							style={{
								margin: "28px 0 0",
								padding: "16px 18px",
								borderRadius: 12,
								background: "rgba(0,0,0,0.12)",
								fontSize: 15,
								lineHeight: 1.5,
								color: "rgba(255,255,255,0.9)",
								fontWeight: 400,
								textAlign: "center",
							}}
						>
							{t("RESULTS.NO_POINTS_BANNER")}
						</p>
					) : null}
				</div>
			</main>
		</div>
	);
}

export default injectIntl(Results);
